import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useRequestTypesAdmin (SOUND-544) — the request-type vocabulary for the
 * /settings/request-types admin. Internal-only `/ticket-request-types`.
 * NOT the create-modal picker: that reads `GET /tickets/request-types`
 * (useTicketRequestTypes), which omits deactivated types.
 *
 *   GET /ticket-request-types?includeInactive=true → { data: [requestType] }
 */
export function useRequestTypesAdmin({ includeInactive = true } = {}) {
  const [requestTypes, setRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchAll = useCallback(async () => {
    if (!companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.listTicketRequestTypesAdmin(includeInactive ? { includeInactive: "true" } : {}),
        auth.user,
        companyId
      );
      setRequestTypes(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, auth?.user, includeInactive]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { requestTypes, loading, error, refetch: fetchAll };
}

/** One request type by key. No-ops for falsy / "new". */
export function useRequestTypeAdmin(key) {
  const [requestType, setRequestType] = useState(null);
  const [loading, setLoading] = useState(!!key && key !== "new");
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchOne = useCallback(async () => {
    if (!key || key === "new" || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest("get", apiRoutes.getTicketRequestTypeAdmin(key), auth.user, companyId);
      setRequestType(res.data?.data || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [key, companyId, auth?.user]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { requestType, loading, error, refetch: fetchOne };
}
